import { useEffect } from "react";

import { AnalyticsContext, useAnalyticsEvents } from "@atlaskit/analytics-next";

import { SignupProvisioningModule } from "@/components/signup/bxp-ui-signup/provisioning";
import { useSearchParameters } from "@/components/signup/hooks";
import {
    UfoCustomTimingKey,
    ufoEventRedirectToProduct,
    getIdentityBaseUrl,
    ProductKey
} from "@/components/signup/utils";
import {
    AvailableProductsResponse,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src";
import {
    checkFeatureFlagGate,
    featureFlags as featureFlagsManifest
} from "@/utils/feature-flags";
import { useGraphqlClient } from "@/utils/graphql";
import {
    JourneyLocalStorage,
    useAnchorLocalStorage
} from "@/utils/local-storage-hook";
import { useLocation } from "@/utils/location-provider";
import { useLoggedInUser } from "@/utils/logged-in-user-provider.tsx";

import { ClientOnly } from "../../../../../../utils/client-only";

import {
    CloudStatusBundleRedirectionProvider,
    CloudStatusRedirectionProvider,
    JiraTemplateChoiceProvider,
    GupsProvider,
    CloudStatusDirectBuyRedirectionProvider
} from "./hooks";
import { CloudStatusProvider, FeatureFlags } from "./hooks";
import { ConfirmationPageProvider } from "./hooks/use-confirmation-page";
import { updateUserProfile } from "./services/update-user-profile";
import { BundleViewSwitch } from "./views/bundle-view-switch";
import { ProductViewSwitch } from "./views/product-view-switch";

const { ApolloProvider } = require("@apollo/client");

const { GRAPHQL_WEBHOOK_SETUP } = UfoCustomTimingKey;

export type ProductData = {
    readonly productKey: string;
    readonly productName: string;
    readonly productLogoUrl?: string;
    readonly productIconUrl?: string;
};

export type ProductDataType = {
    readonly [index: string]: ProductData;
};

export type ConfirmationPageTemplateProps = {
    readonly isProduction: boolean;
    readonly featureFlags: FeatureFlags;
    readonly availableProducts?: AvailableProductsResponse;
};

/* istanbul ignore next */
export const ConfirmationPageTemplate = ({
    isProduction,
    featureFlags,
    availableProducts
}: ConfirmationPageTemplateProps) => {
    const {
        products,
        cloudId,
        provisioningRequestId,
        onDemandUrl,
        platform,
        requestId,
        orchestrationId
    } = useSearchParameters();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const userInfo = useLoggedInUser();
    const location = useLocation();

    const productKey = (
        products.length > 0 ? products[0] : undefined
    ) as ProductKey;
    const bundleProductKeys =
        products.length > 0
            ? products.map((product) => product as ProductKey).join(",")
            : "";

    const { apolloClient } = useGraphqlClient({
        reactComponent: "ConfirmationPageTemplate",
        orchestrationId,
        logParams: { productKey, bundleProductKeys }
    });
    ufoEventRedirectToProduct.endSubstep(GRAPHQL_WEBHOOK_SETUP);
    const isDirectBuy = location.pathname === "/buy/cloud/confirmation";

    useEffect(() => {
        /**
         * Start UFO to measure the performance and reliability
         * of product landing from confirmation page
         *   - https://go/signup-slo
         *   - https://go/signup-ufo
         **/
        // @ts-ignore
        ufoEventRedirectToProduct.start({ productKey, bundleProductKeys });
        ufoEventRedirectToProduct.setCustomData({
            provisioningRequestId,
            cloudId,
            orchestrationId
        });
    }, []); // Empty dependency array required to run once for analytics, initialization, etc.

    // ATLAS-61644: update user profile with local storage team type
    const stargateClient = useBxpWebStargateClient();
    const { getAnchorLocalStorage } = useAnchorLocalStorage();
    useEffect(() => {
        const anchorLocalStorage = getAnchorLocalStorage();
        if (anchorLocalStorage) {
            try {
                const localStorageObj: JourneyLocalStorage =
                    JSON.parse(anchorLocalStorage);

                if (localStorageObj && localStorageObj.team) {
                    updateUserProfile({
                        selectedTeamType: localStorageObj.team,
                        stargateClient
                    });
                }
            } catch (err) {
                console.log(err);
            }
        }
    }, []);

    /*
     * when the user is logged out coming to confirmation page causing errors
     * re-directing them to identity to login and then comeback.
     */

    useEffect(() => {
        const loginUrl = `${getIdentityBaseUrl()}/login?`;
        if (!userInfo.requestInProgress && !userInfo.isUserLoggedIn) {
            window.location.assign(
                `${loginUrl}continue=${encodeURIComponent(
                    window.location.href
                )}`
            );
        }
    }, [userInfo]);

    // Don't show the UI if the user is not logged in.
    // The user will be redirected to the login page if they're not logged in.
    if (!userInfo.isUserLoggedIn) {
        return;
    }

    const jfindConfirmationPageUpdateConfluence = checkFeatureFlagGate(
        featureFlagsManifest.jfindConfirmationPageUpdateConfluence
    );

    // Confluence and JPD loading page
    if (
        products.length === 1 &&
        ((products[0] === "confluence.ondemand" &&
            !jfindConfirmationPageUpdateConfluence) ||
            products[0] === "jira-product-discovery")
    ) {
        // moving away from this
        return (
            <ClientOnly>
                <ApolloProvider client={apolloClient}>
                    <SignupProvisioningModule />
                </ApolloProvider>
            </ClientOnly>
        );
    }

    const defaultAnalyticsData = {
        schemaVersion: 1,
        event: "event", // event verb, in past tense (eg, "clicked")
        eventType: "ui",
        eventComponent: "signUpConfirmationPage", // name of component being interacted with, in camelCase
        eventContainer: "signUpConfirmationPage", // container, in camelCase; eg, component that contains this component within a page
        eventColumn: "signUpConfirmationPage", // should be bundleInformation or signUpForm
        eventName: "signUpConfirmationPageEvent",
        label: "", // the text present on the component being clicked (exact text, not camelCase)
        parentLabel: "", // optional, if this is a nested click (exact text, not camelCase)
        uuid: "signUpConfirmationPage", // required for Magnolia events, unique id for a given component if available
        products
    };

    let template = (
        <CloudStatusRedirectionProvider featureFlags={featureFlags}>
            <ProductViewSwitch />
        </CloudStatusRedirectionProvider>
    );

    if (isDirectBuy) {
        template = (
            <CloudStatusDirectBuyRedirectionProvider>
                <BundleViewSwitch />
            </CloudStatusDirectBuyRedirectionProvider>
        );
    }

    if (products.length > 1) {
        template = (
            <CloudStatusBundleRedirectionProvider>
                <BundleViewSwitch />
            </CloudStatusBundleRedirectionProvider>
        );
    }

    return (
        <ClientOnly>
            <AnalyticsContext data={defaultAnalyticsData}>
                <ConfirmationPageProvider
                    createAnalyticsEvent={createAnalyticsEvent}
                    availableProducts={availableProducts}
                >
                    <JiraTemplateChoiceProvider>
                        <ApolloProvider client={apolloClient}>
                            <GupsProvider>
                                <CloudStatusProvider
                                    isProduction={isProduction}
                                    onDemandUrl={onDemandUrl}
                                    cloudId={cloudId}
                                    platform={platform}
                                    requestId={requestId}
                                    isDirectBuy={isDirectBuy}
                                >
                                    {template}
                                </CloudStatusProvider>
                            </GupsProvider>
                        </ApolloProvider>
                    </JiraTemplateChoiceProvider>
                </ConfirmationPageProvider>
            </AnalyticsContext>
        </ClientOnly>
    );
};
